import { For, Show, createSignal, onCleanup, type Component } from 'solid-js';
import { Panel } from '~ui/panel';
import { Toolbar, ToolbarSection } from '~ui/toolbar';
import { Heading, Text } from '~ui/text';
import { Button } from '~ui/button';
import { Input } from '~ui/input';
import { PlusIcon } from '~ui/icons';
import { copyPortfolio, copying, createPlan, deletePlan, plans, realPortfolio, renamePlan, savingPlan, selectedPlan, setSelectedPlanId } from '~/stores/portfolio-store';

const ROW = 'flex h-row shrink-0 items-center';

// Both destructive actions ask once rather than firing on a single click, and
// disarm themselves after a few seconds so a stray click cannot leave a live
// one sitting there waiting to be hit by the next.
const ArmedButton: Component<{ idle: string; armed: string; title: string; disabled?: boolean; onConfirm: () => void }> = (props) => {
    const [armed, setArmed] = createSignal(false);
    let timer: ReturnType<typeof setTimeout> | undefined;
    onCleanup(() => clearTimeout(timer));

    const disarm = () => {
        clearTimeout(timer);
        setArmed(false);
    };

    const click = () => {
        if (props.disabled) return;
        if (!armed()) {
            setArmed(true);
            timer = setTimeout(disarm, 4000);
            return;
        }
        disarm();
        props.onConfirm();
    };

    return (
        <div class={`${ROW} border-t`}>
            {/* hover:bg-red! — Button's variant hover:bg-muted is emitted later in
                the sheet and would otherwise win, washing an armed destructive
                button out to the muted fill exactly when it needs to look armed. */}
            <Button size='fill' class={`w-full ${armed() ? 'bg-red text-background hover:bg-red!' : ''}`} label={armed() ? props.armed : props.idle} title={props.title} disabled={props.disabled} onClick={click} />
        </div>
    );
};

// The plan list, plus everything you can do to the selected one. Kept in a rail
// beside the plan's own panes rather than in the nav: these actions belong
// to this page, and a delete button that follows you around the app is a
// delete button you eventually hit by accident.
const Plans: Component = () => {
    const source = () => realPortfolio();
    const current = () => selectedPlan();

    return (
        <Panel>
            <Toolbar>
                <ToolbarSection>
                    <Heading level={2} class='pl-2'>
                        Plans
                    </Heading>
                </ToolbarSection>
                <ToolbarSection>
                    <Button size='fill' icon={<PlusIcon />} label='New' title='Start a new plan' disabled={savingPlan()} onClick={() => void createPlan()} />
                </ToolbarSection>
            </Toolbar>

            <div class='flex-1 min-h-0 overflow-y-auto scrollbar-hide'>
                <Show when={plans().length > 0} fallback={<Text tone='muted' class='block px-2 py-2'>No plans yet — New starts one</Text>}>
                    <For each={plans()}>
                        {(plan) => (
                            <button
                                type='button'
                                title={plan.label}
                                onClick={() => setSelectedPlanId(plan.id)}
                                class={`${ROW} w-full cursor-default gap-2 border-b px-2 text-sm ${current()?.id === plan.id ? 'bg-muted-foreground/20 border-b-3 border-foreground' : 'hover:bg-muted'}`}>
                                <span class='truncate'>{plan.label}</span>
                            </button>
                        )}
                    </For>
                </Show>
            </div>

            <Show when={current()}>
                {/* Rename edits the selected plan rather than the row, so a click
                    on a row is only ever a selection — no click target that means
                    "select" on one press and "edit the text" on the next. */}
                <div class={`${ROW} border-t px-2`}>
                    <Text tone='muted' class='shrink-0 pr-2'>
                        Name
                    </Text>
                    <Input bare value={current()!.label} onInput={(e) => renamePlan(current()!.id, e.currentTarget.value)} />
                </div>

                <Show when={source()}>
                    <ArmedButton
                        idle={`Copy from ${source()!.label}`}
                        armed='Overwrite this plan?'
                        title={`Replace this plan's accounts, loans and buys with a copy of ${source()!.label}`}
                        disabled={copying()}
                        onConfirm={() => void copyPortfolio(source()!.id, current()!.id)}
                    />
                </Show>

                <ArmedButton idle='Delete plan' armed='Delete for good?' title='Delete this plan and everything in it' disabled={savingPlan()} onConfirm={() => void deletePlan(current()!.id)} />
            </Show>
        </Panel>
    );
};

export default Plans;
